// Saves a chart from the Informes page as a PNG.
//
// MUI X Charts draw SVG styled through CSS classes, and an SVG serialized on its
// own loses those styles (axis labels turn black, series lose their colour). So
// the computed style of every node is copied onto the clone as inline style
// before it is painted onto a canvas, under a title and the period it covers —
// a PNG pasted into a report has to say what it shows without the page around it.

// The properties that decide how an SVG chart looks. Copying every computed
// property would bloat the file and drag in layout values that mean nothing
// inside an image.
const SVG_STYLE_PROPS = [
  "fill",
  "fill-opacity",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-dasharray",
  "stroke-linecap",
  "stroke-linejoin",
  "opacity",
  "visibility",
  "display",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "letter-spacing",
  "text-anchor",
  "dominant-baseline",
  "alignment-baseline",
  "paint-order",
];

const SCALE = 2; // crisp on retina screens and when a slide scales it up
const PAD = 24;
const TITLE_SIZE = 18;
const SUBTITLE_SIZE = 13;

// "Por dependencia" → "por-dependencia", safe for a file name on any OS.
export const slugify = (text) =>
  String(text || "grafico")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "grafico";

export const chartFileName = (title, rangeKey) =>
  `informe-pqrsd-${slugify(title)}${rangeKey ? `-${rangeKey}` : ""}.png`;

// Geometry the charts animate through CSS rather than attributes: bars get their
// x/y/width/height and line markers their position as style. An SVG rendered as
// an image does not reliably honour those as CSS, so they are written back as
// attributes, which every renderer understands.
const GEOMETRY_PROPS = ["x", "y", "width", "height", "cx", "cy", "r", "rx", "ry"];

const inlineStyles = (source, target) => {
  const computed = window.getComputedStyle(source);
  const style = SVG_STYLE_PROPS
    .map((prop) => {
      const value = computed.getPropertyValue(prop);
      return value ? `${prop}:${value}` : null;
    })
    .filter(Boolean)
    .join(";");
  if (style) target.setAttribute("style", style);

  if (source !== source.ownerSVGElement && source instanceof SVGGraphicsElement) {
    GEOMETRY_PROPS.forEach((prop) => {
      if (!source.style.getPropertyValue(prop)) return;
      const value = parseFloat(computed.getPropertyValue(prop));
      if (Number.isFinite(value)) target.setAttribute(prop, String(value));
    });
    // Line markers are placed by a class rule, bars by inline style, groups by
    // attribute; the computed value covers all three.
    const transform = computed.getPropertyValue("transform");
    if (transform && transform !== "none") target.setAttribute("transform", transform);
  }
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let i = 0; i < sourceChildren.length; i += 1) {
    inlineStyles(sourceChildren[i], targetChildren[i]);
  }
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No fue posible dibujar el gráfico."));
    img.src = src;
  });

/**
 * @param {HTMLElement} container - the element wrapping the chart's <svg>
 * @param {{ title: string, subtitle?: string, fileName: string,
 *           background?: string, color?: string, mutedColor?: string,
 *           fontFamily?: string }} opts
 */
export async function downloadChartPng(container, {
  title,
  subtitle,
  fileName,
  background = "#ffffff",
  color = "#1f1f1f",
  mutedColor = "#6b6b6b",
  fontFamily = "Roboto, Helvetica, Arial, sans-serif",
}) {
  // A chart is one <svg>; skip the small icon SVGs a card may also hold.
  const svg = [...container.querySelectorAll("svg")]
    .sort((a, b) => b.getBoundingClientRect().width * b.getBoundingClientRect().height
      - a.getBoundingClientRect().width * a.getBoundingClientRect().height)[0];
  if (!svg) throw new Error("No hay gráfico para exportar.");

  const { width, height } = svg.getBoundingClientRect();
  const clone = svg.cloneNode(true);
  inlineStyles(svg, clone);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  if (!clone.getAttribute("viewBox")) clone.setAttribute("viewBox", `0 0 ${width} ${height}`);

  const markup = new XMLSerializer().serializeToString(clone);
  const img = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`);

  const header = PAD + TITLE_SIZE + (subtitle ? 8 + SUBTITLE_SIZE : 0) + 16;
  const canvasWidth = Math.ceil(width + PAD * 2);
  const canvasHeight = Math.ceil(header + height + PAD);

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth * SCALE;
  canvas.height = canvasHeight * SCALE;
  const ctx = canvas.getContext("2d");
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  ctx.textBaseline = "top";
  ctx.fillStyle = color;
  ctx.font = `600 ${TITLE_SIZE}px ${fontFamily}`;
  ctx.fillText(title, PAD, PAD);
  if (subtitle) {
    ctx.fillStyle = mutedColor;
    ctx.font = `400 ${SUBTITLE_SIZE}px ${fontFamily}`;
    ctx.fillText(subtitle, PAD, PAD + TITLE_SIZE + 8);
  }

  ctx.drawImage(img, PAD, header, width, height);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("No fue posible generar la imagen.");
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
